/**
 * Cross-checks for the misalignments the 2026-07/08 login incidents taught us
 * to recognise (roles written to dying uids, stranded provisioning, tombstoned
 * owners of live clients).
 *
 * Split in two on purpose:
 *  - findUserDocInconsistencies(user): every check derivable from the users doc
 *    ALONE. Cheap enough to run per-row on the admin Users LIST (400 rows, zero
 *    extra reads) — the "what am I looking at?" surface.
 *  - findUserInconsistencies(user, linkedClient): the full set, adding checks
 *    that need the linked client doc. This is the per-user detail screen's
 *    banner, where the client is already loaded.
 *
 * The weekly credential-health cron is the "everything" sweep; these are the
 * in-context views of the same class of problem.
 */

export const STRANDED_PROVISIONING_FINDING = 'Verified human with explicit client role but NO client — provisioning should have fired; possibly stranded.';
export const PRE_INTENT_GATE_FINDING = 'Has a linked client but roles lack "client" — pre-intent-gate doc; re-provisioning would be blocked if the client were ever lost.';

/** Checks needing ONLY the users doc — safe for list rows. */
export function findUserDocInconsistencies(user) {
  if (!user) return [];
  const findings = [];
  const roles = Array.isArray(user.roles) ? user.roles : [];
  const hasClientRole = roles.includes('client');
  const isCustomerOnly = roles.includes('customer') && !hasClientRole;

  if (user.deleted === true && hasClientRole) {
    findings.push('Sign-in was deleted (deleted:true) but roles still include "client" — the role landed just as the login was being removed.');
  }
  if (user.status === 'client_created' && !user.clientId) {
    findings.push('status is client_created but no clientId is stamped on the user doc.');
  }
  if (user.clientId && !hasClientRole && !user.deleted) {
    findings.push(PRE_INTENT_GATE_FINDING);
  }
  // Named pieces, so each check below reads as a sentence.
  const fullyVerified = user.isHuman === true && user.emailVerified === true;
  const wantsClientButHasNone = hasClientRole && !isCustomerOnly && !user.clientId;
  const noRecordedProvider = !user.authProvider
    && (!Array.isArray(user.authProviders) || user.authProviders.length === 0);

  if (!user.deleted && wantsClientButHasNone && fullyVerified) {
    findings.push(STRANDED_PROVISIONING_FINDING);
  }
  if (isCustomerOnly && user.clientId) {
    findings.push('Customer-only roles but a clientId is stamped — role/entity mismatch.');
  }
  if (!user.deleted && !user.isAnonymous && user.isHuman === true && noRecordedProvider) {
    findings.push('Marked human with no auth provider recorded and not deleted — either this record predates login-method tracking, or the login was removed without the deletion ever being recorded.');
  }
  return findings;
}

/** Full set — doc-only checks plus the ones that need the linked client. */
export function findUserInconsistencies(user, linkedClient) {
  if (!user) return [];
  let findings = findUserDocInconsistencies(user);

  // Pre-attribution veterans have no clientId stamp, so the doc-only reading says
  // "possibly stranded" — but if the caller resolved their client anyway (ownerId
  // lookup), the client's existence disproves that finding. The stamp gap itself
  // heals on restore.
  if (!user.clientId && linkedClient) {
    findings = findings.filter((f) => f !== STRANDED_PROVISIONING_FINDING);
  }
  // Same reasoning for the missing-role veteran: while the client exists, the old
  // "empty roles = assumed client" convention still holds everywhere at runtime, so a
  // red needs-review banner would cry wolf on thousands of veteran docs. The detail
  // screen shows an informational note instead (list rows keep flagging — they can't
  // resolve the client). The real fix is the deliberate roles+clientId backfill.
  if (linkedClient) {
    findings = findings.filter((f) => f !== PRE_INTENT_GATE_FINDING);
  }

  if (user.deleted === true && user.clientId && linkedClient && linkedClient.status === 'live') {
    findings.push('Sign-in was deleted but the linked client is still LIVE — the owner cannot log in to it (restore panel below).');
  }
  if (user.clientId && !linkedClient) {
    findings.push(`clientId ${user.clientId} is stamped but the client doc could not be loaded (missing or unreadable).`);
  }
  // Terms accepted but no IP evidence: the acceptedterms call is fire-and-forget
  // with a short timeout, so it CAN fail silently — leaving a compliance evidence
  // gap (Stripe Connect US requires the acceptance IP) and a hole in fraud
  // correlation. Empty string / whitespace counts as missing.
  if (linkedClient && linkedClient.agreeTerms === true
      && !`${linkedClient.agreedTermsIP || ''}`.trim()) {
    findings.push('Terms accepted but no agreed-terms IP recorded — the IP call failed or timed out (compliance evidence gap; required for Stripe Connect US).');
  }
  return findings;
}
